export type PlayerIncidentType =
  | 'player.startup_failure'
  | 'player.rebuffer'
  | 'player.decode_stall'
  | 'player.mse_pipeline_error'
  | 'player.fatal_video_error';

export type PlayerIncidentSeverity = 'info' | 'warning' | 'error';

export type PlayerIncident = {
  incident_id: string;
  type: PlayerIncidentType;
  severity: PlayerIncidentSeverity;
  message: string;
  occurred_at: string; // ISO
  duration_ms?: number;
  meta?: Record<string, any>;
  player?: Record<string, any>;
};

export type PlayerIncidentTrackerDeps = {
  video: HTMLVideoElement;
  nowMs?: () => number;
  nowIso?: () => string;
  uuid?: () => string;
  bufferedEndSec?: () => number;
  mseImplName?: () => string | null;
  getStageContext?: () => { stage?: string; stateId?: string | null; inAction?: boolean } | null;
  onIncident: (inc: PlayerIncident) => void;
};

const DEFAULTS = {
  tickMs: 400,
  minFreezeMs: 900,
  minIncidentMs: 600,
  bufferMarginRebufferSec: 0.3,
  minGapSameTypeMs: 20_000, // avoid spamming same incident type too often
} as const;

function safeNum(x: any, fallback = 0): number {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

function safeStr(x: any): string {
  return String(x ?? '');
}

function computeBufferedEnd(video: HTMLVideoElement): number {
  try {
    const b = video.buffered;
    if (!b || b.length === 0) return 0;
    return b.end(b.length - 1);
  } catch {
    return 0;
  }
}

/**
 * PlayerIncidentTracker
 * - Event-driven incidents (no periodic summaries).
 * - Confirms stalls by observing playback progress, not just waiting/stalled events.
 * - Sends a single incident per episode, with duration and minimal context.
 */
export class PlayerIncidentTracker {
  private deps: PlayerIncidentTrackerDeps;
  private timer: any = null;
  private destroyed = false;

  private lastCt: number = 0;
  private lastAdvanceAt: number = 0;
  private lastTickAt: number = 0;

  private waitingSignals: number = 0;
  private stalledSignals: number = 0;

  private openIncident:
    | null
    | {
        id: string;
        type: PlayerIncidentType;
        startedAtMs: number;
        startedAtIso: string;
        startCt: number;
        startBufferedEnd: number;
        startMargin: number;
        snapshot: Record<string, any>;
      } = null;

  private lastEmittedAtByType: Record<string, number> = {};

  constructor(deps: PlayerIncidentTrackerDeps) {
    this.deps = deps;
    const now = this.nowMs();
    try { this.lastCt = safeNum(this.deps.video.currentTime, 0); } catch { this.lastCt = 0; }
    this.lastAdvanceAt = now;
    this.lastTickAt = now;
  }

  private nowMs(): number {
    try { return this.deps.nowMs ? this.deps.nowMs() : Date.now(); } catch { return Date.now(); }
  }
  private nowIso(): string {
    try { return this.deps.nowIso ? this.deps.nowIso() : new Date().toISOString(); } catch { return new Date().toISOString(); }
  }
  private uuid(): string {
    try { return this.deps.uuid ? this.deps.uuid() : ((globalThis as any).crypto?.randomUUID ? (globalThis as any).crypto.randomUUID() : `${Date.now()}-${Math.random()}`); } catch { return `${Date.now()}-${Math.random()}`; }
  }

  private getBufferedEnd(): number {
    try {
      if (this.deps.bufferedEndSec) return safeNum(this.deps.bufferedEndSec(), 0);
    } catch {}
    return computeBufferedEnd(this.deps.video);
  }

  private shouldConsiderPlayback(): boolean {
    const v = this.deps.video;
    try {
      if (!v) return false;
      if ((document as any)?.visibilityState && (document as any).visibilityState !== 'visible') return false;
      if (v.paused) return false;
      if ((v as any).seeking) return false;
      return true;
    } catch {
      return false;
    }
  }

  private getSnapshot(extra?: Record<string, any>): Record<string, any> {
    const v = this.deps.video;
    const ct = (() => { try { return safeNum(v.currentTime, 0); } catch { return 0; } })();
    const be = this.getBufferedEnd();
    const margin = Math.max(0, be - ct);
    const stage = (() => { try { return this.deps.getStageContext?.() || null; } catch { return null; } })();
    const mseImpl = (() => { try { return this.deps.mseImplName?.() || null; } catch { return null; } })();

    return {
      ts: this.nowIso(),
      currentTime: ct,
      bufferedEnd: be,
      bufferMarginSec: margin,
      readyState: (() => { try { return safeNum((v as any).readyState, -1); } catch { return -1; } })(),
      networkState: (() => { try { return safeNum((v as any).networkState, -1); } catch { return -1; } })(),
      paused: (() => { try { return Boolean(v.paused); } catch { return null; } })(),
      seeking: (() => { try { return Boolean((v as any).seeking); } catch { return null; } })(),
      currentSrc: (() => { try { return safeStr((v as any).currentSrc || (v as any).src || ''); } catch { return ''; } })(),
      mseImpl,
      stage: stage?.stage || null,
      stateId: stage?.stateId || null,
      inAction: stage?.inAction === true,
      waitingSignals: this.waitingSignals,
      stalledSignals: this.stalledSignals,
      ...(extra || {}),
    };
  }

  private rateLimit(type: string): boolean {
    const now = this.nowMs();
    const last = safeNum(this.lastEmittedAtByType[type], 0);
    if (now - last < DEFAULTS.minGapSameTypeMs) return false;
    this.lastEmittedAtByType[type] = now;
    return true;
  }

  private maybeCloseIncident(reason: string) {
    if (!this.openIncident) return;
    const now = this.nowMs();
    const dur = Math.max(0, now - this.openIncident.startedAtMs);
    // ignore micro-stalls
    if (dur < DEFAULTS.minIncidentMs) {
      this.openIncident = null;
      this.waitingSignals = 0;
      this.stalledSignals = 0;
      return;
    }

    // We rely on tracker rate-limit (not captureError dedup) for incidents.
    if (!this.rateLimit(this.openIncident.type)) {
      this.openIncident = null;
      this.waitingSignals = 0;
      this.stalledSignals = 0;
      return;
    }

    const endSnap = this.getSnapshot({ endReason: reason });
    const inc: PlayerIncident = {
      incident_id: this.openIncident.id,
      type: this.openIncident.type,
      severity: this.openIncident.type === 'player.rebuffer' || this.openIncident.type === 'player.decode_stall' ? 'warning' : 'error',
      message: this.openIncident.type,
      occurred_at: this.openIncident.startedAtIso,
      duration_ms: dur,
      player: {
        current_time: this.openIncident.startCt,
        buffered_end: this.openIncident.startBufferedEnd,
        buffered_margin_sec: this.openIncident.startMargin,
        ready_state: this.openIncident.snapshot.readyState,
        network_state: this.openIncident.snapshot.networkState,
        current_src: this.openIncident.snapshot.currentSrc,
        mse_impl: this.openIncident.snapshot.mseImpl,
      },
      meta: {
        incident_id: this.openIncident.id,
        started_at: this.openIncident.startedAtIso,
        duration_ms: dur,
        start: this.openIncident.snapshot,
        end: endSnap,
      },
    };

    this.openIncident = null;
    this.waitingSignals = 0;
    this.stalledSignals = 0;
    try { this.deps.onIncident(inc); } catch {}
  }

  private maybeOpenIncident() {
    if (this.openIncident) return;
    if (!this.shouldConsiderPlayback()) return;

    const now = this.nowMs();
    const v = this.deps.video;
    const ct = (() => { try { return safeNum(v.currentTime, 0); } catch { return 0; } })();
    const be = this.getBufferedEnd();
    const margin = Math.max(0, be - ct);
    const stalledForMs = now - this.lastAdvanceAt;
    if (stalledForMs < DEFAULTS.minFreezeMs) return;

    const type: PlayerIncidentType = (margin < DEFAULTS.bufferMarginRebufferSec) ? 'player.rebuffer' : 'player.decode_stall';
    const id = this.uuid();
    const snap = this.getSnapshot({ stalledForMs });
    this.openIncident = {
      id,
      type,
      startedAtMs: now,
      startedAtIso: this.nowIso(),
      startCt: ct,
      startBufferedEnd: be,
      startMargin: margin,
      snapshot: snap,
    };
  }

  private tick() {
    if (this.destroyed) return;
    const now = this.nowMs();
    if (now - this.lastTickAt < DEFAULTS.tickMs - 50) return;
    this.lastTickAt = now;

    const v = this.deps.video;
    const ct = (() => { try { return safeNum(v.currentTime, 0); } catch { return this.lastCt; } })();
    const advanced = Math.abs(ct - this.lastCt) > 0.001;
    if (advanced) {
      this.lastCt = ct;
      this.lastAdvanceAt = now;
      // Close stall incident on first progress.
      this.maybeCloseIncident('progress');
      return;
    }

    // If not advancing, maybe open an incident (confirmed by time).
    this.maybeOpenIncident();
  }

  noteWaitingSignal() {
    this.waitingSignals++;
    // do not open incident solely on signal; only helps debugging context
  }

  noteStalledSignal() {
    this.stalledSignals++;
  }

  onFatalVideoError(code: number | null, extra?: Record<string, any>) {
    if (!this.rateLimit('player.fatal_video_error')) return;
    const id = this.uuid();
    const snap = this.getSnapshot({ code, ...(extra || {}) });
    const inc: PlayerIncident = {
      incident_id: id,
      type: 'player.fatal_video_error',
      severity: 'error',
      message: 'player.fatal_video_error',
      occurred_at: this.nowIso(),
      meta: { incident_id: id, snapshot: snap },
      player: {
        error_code: code ?? null,
        current_time: snap.currentTime,
        buffered_end: snap.bufferedEnd,
        buffered_margin_sec: snap.bufferMarginSec,
        ready_state: snap.readyState,
        network_state: snap.networkState,
        current_src: snap.currentSrc,
        mse_impl: snap.mseImpl,
      },
    };
    try { this.deps.onIncident(inc); } catch {}
  }

  onStartupFailure(seconds: number, extra?: Record<string, any>) {
    if (!this.rateLimit('player.startup_failure')) return;
    const id = this.uuid();
    const snap = this.getSnapshot({ seconds, ...(extra || {}) });
    const inc: PlayerIncident = {
      incident_id: id,
      type: 'player.startup_failure',
      severity: 'error',
      message: 'player.startup_failure',
      occurred_at: this.nowIso(),
      meta: { incident_id: id, snapshot: snap },
      player: {
        current_time: snap.currentTime,
        buffered_end: snap.bufferedEnd,
        buffered_margin_sec: snap.bufferMarginSec,
        ready_state: snap.readyState,
        network_state: snap.networkState,
        current_src: snap.currentSrc,
        mse_impl: snap.mseImpl,
      },
    };
    try { this.deps.onIncident(inc); } catch {}
  }

  start() {
    if (this.destroyed) return;
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), DEFAULTS.tickMs);
  }

  stop() {
    if (!this.timer) return;
    try { clearInterval(this.timer); } catch {}
    this.timer = null;
  }

  flushOnPageHide() {
    // If an incident is open, close it best-effort on page hide.
    this.maybeCloseIncident('pagehide');
  }

  destroy() {
    this.destroyed = true;
    this.stop();
    this.openIncident = null;
  }
}

